import React, { useEffect, useState } from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { resolveUploadUrl } from '../UserPhoto';

/**
 * Reusable cell renderers for <DataTable>.
 * Use these inside a column's `render(row)` to keep tables consistent.
 */

// Deterministic soft avatar palette (bg + text) picked from the name.
const AVATAR_PALETTE = [
  { bg: '#EEF3FF', fg: '#2563EB' },
  { bg: '#F3EEFF', fg: '#7C3AED' },
  { bg: '#E7FBF6', fg: '#0D9488' },
  { bg: '#FFF4ED', fg: '#EA580C' },
  { bg: '#FDF0F6', fg: '#DB2777' },
  { bg: '#EEF0FF', fg: '#4F46E5' },
];

const initialsOf = (name) => {
  const p = (name || '').replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
};

const paletteFor = (name) => {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

// Rounded initials avatar (or photo when `image` is provided).
/**
 * A person's picture, or their initials. `image` is the STORED value — a
 * root-relative "/uploads/…" or "/api/files/…" path (served by the API host,
 * not the dashboard's), or an absolute URL — so it is resolved first. A file
 * that fails to load (deleted, moved, offline) falls back to the initials
 * instead of showing a broken-image icon with the name spilling out of it.
 */
export const Avatar = ({ name, image, size = 38, className = '' }) => {
  const style = { width: size, height: size };
  const url = resolveUploadUrl(image);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  if (url && !failed) {
    return <img src={url} alt="" style={style} onError={() => setFailed(true)} className={`rounded-xl object-cover shrink-0 ${className}`} />;
  }
  const pal = paletteFor(name);
  return (
    <span
      style={{ ...style, background: pal.bg, color: pal.fg }}
      className={`rounded-xl grid place-items-center text-[12px] font-extrabold shrink-0 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.05)] ${className}`}
    >
      {initialsOf(name)}
    </span>
  );
};

// Avatar + primary name + optional sub-line (e.g. email). Ideal for the first column.
export const Person = ({ name, sub, image }) => (
  <div className="flex items-center gap-3 min-w-0">
    <Avatar name={name} image={image} />
    <div className="min-w-0">
      <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 leading-tight truncate">{name}</div>
      {sub && <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate">{sub}</div>}
    </div>
  </div>
);

// Semantic status pill. `tone`: good | info | warn | crit | mute.
const TONE = {
  good: 'text-[#059669] bg-[#ECFDF5] border-[#CFF3E4] dark:text-emerald-400 dark:bg-emerald-900/10 dark:border-emerald-900/50',
  info: 'text-[#2563EB] bg-[#EEF3FF] border-[#D8E4FF] dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-900/50',
  warn: 'text-[#D97706] bg-[#FFFBEB] border-[#FBE3C0] dark:text-amber-400 dark:bg-amber-900/10 dark:border-amber-900/50',
  crit: 'text-[#E11D48] bg-[#FFF1F3] border-[#FFDCE3] dark:text-red-400 dark:bg-red-900/10 dark:border-red-900/50',
  mute: 'text-[#64748B] bg-[#F1F5F9] border-[#E2E8F0] dark:text-gray-400 dark:bg-[#31373f] dark:border-gray-700',
};
const TONE_DOT = {
  good: 'bg-[#059669]', info: 'bg-[#2563EB]', warn: 'bg-[#D97706]', crit: 'bg-[#E11D48]', mute: 'bg-[#94A3B8]',
};

export const StatusPill = ({ tone = 'mute', dot = true, children }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${TONE[tone] || TONE.mute}`}>
    {dot && <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[tone] || TONE_DOT.mute}`} />}
    {children}
  </span>
);

// View / Edit / Delete icon buttons. Only the handlers you pass are rendered.
const ACT = 'w-[30px] h-[30px] grid place-items-center rounded-[9px] border border-transparent bg-transparent text-[#94A3B8] transition group-hover/row:border-[#E3E9F3] group-hover/row:bg-white dark:group-hover/row:bg-[#262b33] dark:group-hover/row:border-gray-700';

export const RowActions = ({ onView, onEdit, onDelete }) => {
  const stop = (fn) => (e) => { e.stopPropagation(); if (fn) fn(e); };
  return (
    <div className="flex items-center justify-end gap-1.5">
      {onView && (
        <button type="button" onClick={stop(onView)} title="View" className={`${ACT} hover:!text-[#2563EB] hover:!bg-[#EEF3FF] hover:!border-[#D8E4FF]`}>
          <Eye className="w-[15px] h-[15px]" />
        </button>
      )}
      {onEdit && (
        <button type="button" onClick={stop(onEdit)} title="Edit" className={`${ACT} hover:!text-[#2563EB] hover:!bg-[#EEF3FF] hover:!border-[#D8E4FF]`}>
          <Pencil className="w-[15px] h-[15px]" />
        </button>
      )}
      {onDelete && (
        <button type="button" onClick={stop(onDelete)} title="Delete" className={`${ACT} hover:!text-[#E11D48] hover:!bg-[#FFF1F3] hover:!border-[#FFDCE3]`}>
          <Trash2 className="w-[15px] h-[15px]" />
        </button>
      )}
    </div>
  );
};
